import { Injectable } from "@angular/core";
import { ItemWithRelations, FileWithRelations } from "../api/models";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class PictureService {
  cdnUrl = environment.cdnUrl;
  NOT_FOUND_IMAGE = "painting_notfound.jpg";

  constructor() {}

    /**
   * Generates the full CDN path for a given filename.
   *
   * @param {string} [filename] - The filename for which to generate the path.
   * @returns {string} - The full CDN path for the given filename.
   */
  getPath(filename?: string): string {
    // If no filename is provided, return the path for the default "not found" image
    if (!filename) {
      return this.getPath("painting_notfound.jpg");
    }

    // Base CDN path with resizing and formatting options
    const CDN_PATH = `${this.cdnUrl}/plain/rs:fit:800:800:1/g:no/`;

    // Construct the S3 path for the given filename
    const path = `s3://inventories.prague/items/pictures/${filename}`;

    // Encode the S3 path to base64
    const encodedFileName = btoa(path);

    // Construct the full image path with the CDN path and encoded filename
    const imagePath = CDN_PATH + encodedFileName + ".webp";

    return imagePath;
  }

  /**
   * Retrieves all pictures associated with the given item, optionally filtering by public visibility.
   *
   * @param {ItemWithRelations} item - The item containing the files to filter.
   * @param {boolean} [publicOnly=true] - Whether to include only public pictures.
   * @returns {FileWithRelations[]} - An array of pictures associated with the item.
   */
  getAllPictures(
    item: ItemWithRelations,
    publicOnly: boolean = true
  ): FileWithRelations[] {
    const pictures: FileWithRelations[] = [];
    const files = item.files;

    if (files) {
      // Filter files to include only those of type 1 (pictures)
      const picsOnly = files.filter((a) => a.type === 1);

      // Filter public pictures and sort them by order
      const publicPictures = picsOnly
        .filter((a) => a.isPublic === true)
        .sort((a, b) => a.order - b.order);

      // If publicOnly is false, filter private pictures and sort them by order
      const privatePictures = !publicOnly
        ? picsOnly
            .filter((a) => a.isPublic === false)
            .sort((a, b) => a.order - b.order)
        : [];

      // Add public and private pictures to the pictures array
      pictures.push(...publicPictures);
      pictures.push(...privatePictures);
    }

    return pictures;
  }

  /**
   * Parses all picture URLs associated with the given item, optionally filtering by public visibility.
   *
   * @param {ItemWithRelations} item - The item containing the files to filter.
   * @param {boolean} [publicOnly=true] - Whether to include only public pictures.
   * @returns {string[]} - An array of URLs for the pictures associated with the item.
   */
  parseAllPicturesUrl(
    item: ItemWithRelations,
    publicOnly: boolean = true
  ): string[] {
    // Retrieve all pictures associated with the item
    const pictures: FileWithRelations[] = this.getAllPictures(item, publicOnly);

    // Initialize an array to hold the picture URLs
    const paths: string[] = [];

    // Convert each picture filename to a URL and add it to the paths array
    pictures.forEach((picture) => {
      const path = this.getPath(picture.filename);
      paths.push(path);
    });

    // If no pictures are found, add a default "not found" image URL
    if (paths.length === 0) {
      paths.push(this.getPath("painting_notfound.jpg"));
    }

    // Return the array of picture URLs
    return paths;
  }

  /**
   * Parses the main picture URL associated with the given item.
   *
   * @param {ItemWithRelations} item - The item containing the files to filter.
   * @returns {string} - The URL of the main picture associated with the item.
   */
  parsMainPictureUrl(item: ItemWithRelations): string {
    let mainPicture: FileWithRelations | undefined = undefined;
    const files = item.files;

    if (files) {
      // Filter files to include only those of type 1 (pictures)
      const pictures = files.filter((a) => a.type === 1);

      // Filter public pictures and sort them by order
      const publicPictures = pictures
        .filter((a) => a.isPublic)
        .sort((a, b) => a.order - b.order);

      // Filter private pictures and sort them by order
      const privatePictures = pictures
        .filter((a) => !a.isPublic)
        .sort((a, b) => a.order - b.order);

      // Select the first public picture as the main picture
      if (publicPictures.length > 0) {
        mainPicture = publicPictures[0];
      }
      // If no public pictures are available, select the first private picture as the main picture
      else if (privatePictures.length > 0) {
        mainPicture = privatePictures[0];
      }
    }

    // Get the path of the main picture or a default "not found" image if no main picture is available
    const path = this.getPath(
      mainPicture ? mainPicture.filename : this.NOT_FOUND_IMAGE
    );

    return path;
  }

  /**
   * Retrieves the full filename of the main picture associated with an item.
   *
   * @param item The item with relations.
   * @returns The full filename of the main picture, or undefined if no main picture is found.
   */
  getMainPictureFullFilename(item: ItemWithRelations): string | undefined {
    // Initialize mainPicture as undefined
    let mainPicture: FileWithRelations | undefined = undefined;

    // Retrieve the files associated with the item
    const files = item.files;

    // Check if files exist
    if (files) {
      // Filter pictures from files
      const pictures = files.filter((file) => file.type === 1);

      // Filter public pictures and sort by order
      const publicPictures = pictures
        .filter((file) => file.isPublic)
        .sort((a, b) => a.order - b.order);

      // Filter private pictures and sort by order
      const privatePictures = pictures
        .filter((file) => !file.isPublic)
        .sort((a, b) => a.order - b.order);

      // Set mainPicture to the first public picture if available, otherwise to the first private picture
      if (publicPictures.length > 0) {
        mainPicture = publicPictures[0];
      } else if (privatePictures.length > 0) {
        mainPicture = privatePictures[0];
      }
    }

    // Return the full filename of the main picture, or undefined if no main picture is found
    return mainPicture ? mainPicture.filename + mainPicture.ext : this.NOT_FOUND_IMAGE;
  }
}
